import {Box, Button, SxProps, Theme} from '@mui/material';
import {useCan, usePersona} from 'src/permissions/hooks';
import {FC, useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import {useParams} from 'react-router-dom';
import {contractsApi, tariffsApi} from 'src/api';
import {useAppDispatch, useAppSelector, useDefineIsRealEstateRole} from 'src/app/hooks';
import {AppDispatch, store} from 'src/app/store';
import {FeedbackForm} from '../../components/forms/FeedbackForm';
import AMPModal from '../../components/common/AMPModal';
import {ContractIdentityCard} from 'src/components/contracts/ContractIdentityCard';
import {ContractPlanCard} from 'src/components/contracts/ContractPlanCard';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {Signature} from 'src/components/common/Signature';
import {ContractStatusEnum} from 'src/enums';
import {PdfDocument} from 'src/components/common/PdfDocument';
import {archiveButtonSx, changePlanBtn} from './ContractInfo.sx';
import {MoveOperationButton} from 'src/components/contracts/MoveOperationButton';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError, NotifySuccess} from 'src/utils/notification';
import {AuthUserDTO, ContractByIdRequest, ContractPdfDataRequest, FeedbackTypeEnum} from '../../types';
import {useGetUserLanguage} from '../../hooks/useGetUserLanguage';

const pageSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    // Clears the app bar, as every other page does now that its header lives in the first card.
    mt: 2.5,
};

type GetContractByIdQueryTrigger = ReturnType<typeof contractsApi.useLazyGetContractByIdQuery>[0];
type SignContractMutationTrigger = ReturnType<typeof contractsApi.useSignContractMutation>[0];

const handleGetContractInfo =
    (trigger: GetContractByIdQueryTrigger) =>
        async (businessId: number, contractId: number, userId?: number) => {
            const params: ContractByIdRequest = {
                businessId,
                contractId,
                userId,
            };
            if (!userId) {
                delete params.userId;
            }
            // Neither the company nor the property is named here. The endpoint takes both as request
            // parameters and the server overwrites whichever matches the caller's reach, so passing an
            // account's own would at best agree with what the server already decided.

            store.dispatch(preloaderChangeStatus(true));
            try {
                return await trigger(params).unwrap();
            } catch (error) {
                if (isErrorObject(error)) {
                    NotifyError(error.data.message);
                }
            } finally {
                store.dispatch(preloaderChangeStatus(false));
            }
        };

const handleSubmitSignature =
    (
        trigger: SignContractMutationTrigger,
        dispatch: AppDispatch,
        authInfo: AuthUserDTO,
        contractId: number
    ) =>
        async (file: File) => {
            dispatch(preloaderChangeStatus(true));
            try {
                const formData = new FormData();
                formData.append('file', file);
                formData.set('userId', String(authInfo.id));
                await trigger({contractId, formData}).unwrap();
            } catch (error) {
                if (isErrorObject(error)) {
                    NotifyError(error.data.message);
                }
            } finally {
                dispatch(preloaderChangeStatus(false));
            }
        };

export const ContractInfo: FC = () => {
    const {id} = useParams();
    const persona = usePersona();
    const canRequestPlanChange = useCan('profile.sendFeedback');
    const canReadContract = useCan('contracts.read');
    const canSignContract = useCan('contracts.sign');
    const intl = useIntl();
    const [currentLanguage] = useGetUserLanguage();
    const {authInfo, activeBusiness} = useAppSelector((state) => state.common);
    const isCompanyUser = persona.isCompany;
    const realEstateId = authInfo?.realEstate?.id;
    const isRealEstate = useDefineIsRealEstateRole();
    const [trigger, response] = contractsApi.useLazyGetContractByIdQuery();
    const [archiveContract] = contractsApi.useArchiveContractMutation();

    const [getTariff, tariff] = tariffsApi.useLazyGetTariffByIdQuery();
    const [getContarctPdf, contractPdf] = contractsApi.useLazyContractPdfQuery();
    const [pdfAsImage, setPdfAsImage] = useState<string>('');
    const [open, setOpen] = useState<boolean>(false);
    const [triggerSignature] = contractsApi.useSignContractMutation();
    const dispatch = useAppDispatch();

    // The permission is asked for unconditionally and the status is checked after. Behind the `&&`
    // the hook ran only once the contract had loaded and turned out to be in INSTALLATION, so the
    // number of hooks changed between renders and React tore the page down with "prevDeps is
    // undefined" - on exactly those contracts and no others.
    const canActivateContract = useCan('contracts.activate');
    const canChangeStatus =
        response.data?.status === ContractStatusEnum.INSTALLATION && canActivateContract;
    const canArchive = useCan('contracts.archive');

    const canShowChangePlan = Boolean(tariff.data) && canRequestPlanChange;
    const canShowArchive =
        !isRealEstate &&
        !isCompanyUser &&
        response?.data?.status !== ContractStatusEnum.ARCHIVED &&
        canArchive;

    useEffect(() => {
        if (!id) {
            return;
        }

        // Zero, so the helper drops it: a driver reading their own contract has their userId forced
        // onto the request by the server, and seeding it here only looked like the client deciding.
        const userId = 0;

        if (isRealEstate) {
            void handleGetContractInfo(trigger)(activeBusiness, +id, userId);
        } else {
            void handleGetContractInfo(trigger)(activeBusiness, +id, userId);
        }
    }, [id]);

    const getData = async () => {
        if (response.data) {
            await getTariff({
                businessId: response.data.business.id,
                tariffId: response.data.tariff.id,
            }).unwrap();

            if (isCompanyUser && response.data.status === ContractStatusEnum.OPERATION) {
                return;
            }

            if (response.data.status === ContractStatusEnum.WAIT_FOR_SIGNATURE) {
                return;
            }

            const input: ContractPdfDataRequest = {
                businessId: response.data.business.id,
                contractId: response.data.id,
                userId: response.data.user.id,
                realEstateId,
            };


            await getContarctPdf(input).unwrap();
        }
    };

    useEffect(() => {
        void getData();
    }, [response]);

    useEffect(() => {
        if (contractPdf.data) {
            setPdfAsImage(contractPdf.data);
        }
    }, [contractPdf]);

    const handleChangePlanModal = (status: boolean) => {
        return () => setOpen(status);
    };

    const handleArchive = async () => {
        if (!id) {
            return;
        }

        try {
            const formData = new FormData();
            formData.set('businessId', String(response.data?.business?.id ?? ''));
            await archiveContract({contractId: Number(id), formData});
            NotifySuccess('Contract archived');
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data.message);
            }
        }
    };

    return (
        <>
            {response.data && (
                // One column of full-width sections: the contract, the plan it is on, the document.
                // The three actions used to sit in two places and the portrait PDF in two thirds of
                // the width; both were arrangements the page inherited rather than chose.
                <Box sx={pageSx}>
                    <ContractIdentityCard
                        data={response.data}
                        actions={(
                            <>
                                {canChangeStatus && (
                                    <MoveOperationButton
                                        contractId={response.data.id}
                                        businessId={response.data.business.id}
                                    />
                                )}
                                {canShowChangePlan && (
                                    <Button
                                        onClick={handleChangePlanModal(true)}
                                        variant="contained"
                                        sx={changePlanBtn}
                                    >
                                        {intl.formatMessage({id: 'changeMyPlan'})}
                                    </Button>
                                )}
                                {canShowArchive && (
                                    <Button
                                        variant="contained"
                                        onClick={handleArchive}
                                        sx={archiveButtonSx}
                                    >
                                        {intl.formatMessage({id: 'archiveContract'})}
                                    </Button>
                                )}
                            </>
                        )}
                    />

                    {tariff.data && (
                        <ContractPlanCard
                            data={tariff.data}
                            language={currentLanguage}
                        />
                    )}

                    {canReadContract && (
                        <>
                            {response.data.status === ContractStatusEnum.WAIT_FOR_SIGNATURE
                                && canSignContract && (
                                <Signature
                                    handleSubmit={handleSubmitSignature(
                                        triggerSignature,
                                        dispatch,
                                        authInfo,
                                        Number(id)
                                    )}
                                    signature={response.data.userSignatureUrl ?? ''}
                                />
                            )}
                            {response.data.status !== ContractStatusEnum.WAIT_FOR_SIGNATURE && (
                                <PdfDocument
                                    pdfLink={pdfAsImage}
                                    userName={`${response.data.user.firstName}${response.data.user.lastName}`}
                                    businessId={response?.data?.business?.id}
                                    contractId={response?.data?.id}
                                />
                            )}
                        </>
                    )}
                </Box>
            )}
            <AMPModal
                handleClose={handleChangePlanModal(false)}
                open={open}
                title={intl.formatMessage({id: 'changeTariff'})}
                type="lg"
                fullWidth
            >
                <FeedbackForm
                    type={FeedbackTypeEnum.CHANGE_TARIFF}
                    handleClose={handleChangePlanModal(false)}
                />
            </AMPModal>
            {/* TODO: add a contract for this page */}
        </>
    );
};
